import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhotoLibraryService from '../../../services/PhotoLibraryService';
import logger from '../../../utils/logger';
import { formatDateTime } from '../../../utils/dateUtils';

const PAGE_SIZE = 24;

const formatDate = (value) => {
  if (!value) return '—';
  return formatDateTime(value);
};

const ensureArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const Photos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [notification, setNotification] = useState(null);
  const [formValues, setFormValues] = useState({
    title: '',
    description: '',
    ownerName: '',
    userId: '',
    tags: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPhotos(page);
  }, [page]);

  const fetchPhotos = async (requestedPage = 1) => {
    setLoading(true);
    try {
      const response = await PhotoLibraryService.list({ page: requestedPage, limit: PAGE_SIZE });
      setPhotos(response.items || []);
      setTotal(response.total || 0);
    } catch (error) {
      logger.error('[PhotosPage] Failed to load photos:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to load photos'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    if (!formValues.title) {
      setFormValues((prev) => ({
        ...prev,
        title: file.name.replace(/\.[^.]+$/, '')
      }));
    }
  };

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setSelectedFile(null);
    setFormValues({
      title: '',
      description: '',
      ownerName: '',
      userId: '',
      tags: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setNotification({ type: 'warning', message: 'Select a photo to upload.' });
      return;
    }

    const formData = new FormData();
    formData.append('photo', selectedFile);
    if (formValues.title) formData.append('title', formValues.title);
    if (formValues.description) formData.append('description', formValues.description);
    if (formValues.ownerName) formData.append('ownerName', formValues.ownerName);
    if (formValues.userId) formData.append('userId', formValues.userId);
    if (formValues.tags) formData.append('tags', formValues.tags);
    formData.append('source', 'dashboard');

    setUploading(true);
    try {
      await PhotoLibraryService.upload(formData);
      setNotification({ type: 'success', message: 'Photo uploaded successfully.' });
      resetForm();
      await fetchPhotos(page);
    } catch (error) {
      logger.error('[PhotosPage] Upload failed:', error);
      setNotification({ type: 'error', message: error.message || 'Failed to upload photo.' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    const confirmed = window.confirm('Delete this photo permanently?');
    if (!confirmed) return;
    try {
      await PhotoLibraryService.remove(photoId);
      setNotification({ type: 'success', message: 'Photo deleted.' });
      setPhotos((prev) => prev.filter((item) => item.id !== photoId));
      if ((photos.length - 1) === 0 && page > 1) {
        setPage((prev) => prev - 1);
      }
    } catch (error) {
      logger.error('[PhotosPage] Delete failed:', error);
      setNotification({ type: 'error', message: error.message || 'Failed to delete photo.' });
    }
  };

  const handleDownload = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderStatus = () => {
    if (!notification) return null;
    return (
      <Alert
        severity={notification.type}
        onClose={() => setNotification(null)}
      >
        {notification.message}
      </Alert>
    );
  };

  const renderGrid = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress />
        </Box>
      );
    }

    if (!photos.length) {
      return (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No photos yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a photo to populate this library. Photos uploaded by admins or clients will appear here once saved.
            </Typography>
            <Button
              sx={{ mt: 2 }}
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Photo
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {photos.map((photo) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="div"
                  sx={{
                    pt: '56.25%',
                    backgroundImage: `url(${photo.url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {photo.title || 'Untitled'}
                      </Typography>
                      <Chip
                        size="small"
                        label={(photo.uploaded_by || 'admin').toUpperCase()}
                        color={photo.uploaded_by === 'client' ? 'success' : 'primary'}
                      />
                    </Stack>
                    {photo.owner_name && (
                      <Typography variant="body2" color="text.secondary">
                        Client: {photo.owner_name}
                      </Typography>
                    )}
                    {photo.user_id && (
                      <Typography variant="body2" color="text.secondary">
                        User ID: {photo.user_id}
                      </Typography>
                    )}
                    {photo.description && (
                      <Typography variant="body2">
                        {photo.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Uploaded: {formatDate(photo.created_at || photo.createdAt)}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {ensureArray(photo.tags).map((tag) => (
                        <Chip key={`${photo.id}-${tag}`} size="small" label={tag} />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => handleDownload(photo.url)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(photo.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {total > PAGE_SIZE && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={Math.ceil(total / PAGE_SIZE)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={{ padding: '24px', backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
        }}>
          <CloudUploadIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={700} color="#1f2937">Photo Library</Typography>
          <Typography variant="body2" color="text.secondary">Upload photos to the shared library. Images are stored in S3 and tracked in the database.</Typography>
        </Box>
      </Box>

      <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Box flex={1}>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Photo Title"
                  value={formValues.title}
                  onChange={handleInputChange('title')}
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={formValues.description}
                  onChange={handleInputChange('description')}
                  multiline
                  minRows={2}
                  fullWidth
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Client/User ID (optional)"
                    value={formValues.userId}
                    onChange={handleInputChange('userId')}
                    fullWidth
                  />
                  <TextField
                    label="Client Name (optional)"
                    value={formValues.ownerName}
                    onChange={handleInputChange('ownerName')}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Tags (comma separated)"
                  value={formValues.tags}
                  onChange={handleInputChange('tags')}
                  fullWidth
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {selectedFile ? 'Change Photo' : 'Choose Photo'}
                  </Button>
                  {selectedFile && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </Typography>
                  )}
                  <Box flexGrow={1} />
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploading || !selectedFile}
                  >
                    {uploading ? 'Uploading…' : 'Upload Photo'}
                  </Button>
                  <Tooltip title="Refresh library">
                    <span>
                      <IconButton onClick={() => fetchPhotos(page)} disabled={loading}>
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {renderStatus()}
      {renderGrid()}
    </Box>
  );
};

export default Photos;

